import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import './VerifyEmail.css'

export function VerifyEmail() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'idle'>('idle')
  const [message, setMessage] = useState<string>('')
  const [email, setEmail] = useState<string>('')

  useEffect(() => {
    const emailParam = searchParams.get('email')
    const tokenParam = searchParams.get('token')

    if (emailParam && tokenParam) {
      setEmail(emailParam)
      verifyEmail(emailParam, tokenParam)
    } else {
      setStatus('error')
      setMessage('Invalid verification link. Please check your email and try again.')
    }
  }, [searchParams])

  const verifyEmail = async (emailAddress: string, verificationToken: string) => {
    setStatus('verifying')
    setMessage('Verifying your email address...')

    try {
      const response = await api.post<{
        message: string
        tenantId?: string
        subdomain?: string
        status?: string
        estimatedTime?: string
      }>('/verify-email', {
        email: emailAddress,
        token: verificationToken,
      })

      setStatus('success')
      setMessage(response.data.message || 'Email verified successfully!')
    } catch (error: any) {
      console.error('Verification error:', error)
      setStatus('error')
      const errorMessage = error.response?.data?.error || error.message || 'Failed to verify email. Please try again.'
      setMessage(errorMessage)
    }
  }

  const handleResend = async () => {
    if (!email) {
      setMessage('Email address is required to resend verification.')
      return
    }

    setStatus('verifying')
    setMessage('Resending verification email...')

    try {
      await api.post('/resend-verification', { email })
      setStatus('success')
      setMessage('Verification email has been resent. Please check your inbox.')
    } catch (error: any) {
      console.error('Resend error:', error)
      setStatus('error')
      const errorMessage = error.response?.data?.error || error.message || 'Failed to resend verification email.'
      setMessage(errorMessage)
    }
  }

  const handleBackToRegister = () => {
    navigate('/register')
  }

  return (
    <div className="verify-email-container">
      <div className="verify-email-wrapper">
        <div className="verify-email-header">
          <h1>Email Verification</h1>
        </div>

        <div className="verify-email-content">
          {status === 'verifying' && (
            <div className="verification-status verifying">
              <div className="spinner"></div>
              <p>{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="verification-status success">
              <div className="success-icon">✓</div>
              <h2>Email Verified Successfully!</h2>
              <p>{message}</p>
              <p className="info-text">
                Your clinic instance is now being provisioned. This typically takes 5-10 minutes.
                You will receive an email notification when it's ready.
              </p>
              <button
                className="btn-primary"
                onClick={() => navigate('/')}
              >
                Return to Home
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="verification-status error">
              <div className="error-icon">✗</div>
              <h2>Verification Failed</h2>
              <p>{message}</p>
              {email && (
                <div className="verification-actions">
                  <button
                    className="btn-secondary"
                    onClick={handleResend}
                  >
                    Resend Verification Email
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleBackToRegister}
                  >
                    Back to Registration
                  </button>
                </div>
              )}
              {!email && (
                <button
                  className="btn-primary"
                  onClick={handleBackToRegister}
                >
                  Back to Registration
                </button>
              )}
            </div>
          )}

          {status === 'idle' && (
            <div className="verification-status idle">
              <p>Loading...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
